'use client'

import {
  type AppConfig,
  type Formats,
  type NamespaceKeys,
  type TranslationValues,
  useTranslations as useNextIntlTranslations,
} from 'next-intl'
import { useSearchParams } from 'next/navigation'

import isEmpty from 'lodash/fp/isEmpty'
import isNil from 'lodash/fp/isNil'

import type { HTTPError, Translator } from '../types'

import RichText, { getTags } from '../components/RichText'
import {
  translateHTTPError,
  translateIfNotEmpty,
  translateInline,
  translateWithFallback,
} from '../helpers/translations'

const originalRich: Record<string, Translator['rich']> = {}

export default function useTranslations(
  namespace?: NamespaceKeys<AppConfig['Messages'], string>,
) {
  const searchParams = useSearchParams()
  const debugTranslations = !isNil(searchParams?.get('debugTranslations'))

  const actualNamespace = namespace || undefined

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let $t = useNextIntlTranslations(actualNamespace) as any
  if (isNil(originalRich[actualNamespace ?? ''])) {
    originalRich[actualNamespace ?? ''] = $t.rich
  }

  if (debugTranslations) {
    const displayKey = (key: string | null | undefined) => key

    $t = displayKey

    $t.fallback = displayKey
    $t.inline = displayKey
    $t.notEmpty = displayKey
    $t.rich = displayKey
    $t.rich.notEmpty = displayKey
    $t.rich.simple = displayKey
    $t.rich.simple.notEmpty = displayKey

    return $t as Translator
  }

  $t.fallback = (
    key: string | null | undefined,
    fallback: string,
    values?: TranslationValues | undefined,
    formats?: Partial<Formats> | undefined,
  ) => translateWithFallback(key, fallback, $t, values, formats)
  $t.inline = (
    string: string | null | undefined,
    values?: TranslationValues | undefined,
    formats?: Partial<Formats> | undefined,
    fallback?: string | null | undefined,
  ) => translateInline(string, $t, values, formats, fallback)
  $t.notEmpty = (
    key: string | null | undefined,
    values?: TranslationValues | undefined,
    formats?: Partial<Formats> | undefined,
  ) => translateIfNotEmpty(key, $t, values, formats)
  $t.rich = (
    key: string | null | undefined,
    values?: TranslationValues | undefined,
    formats?: Partial<Formats> | undefined,
  ) => {
    if (isEmpty(key)) {
      return false
    }

    return (
      <RichText>
        {(tags) =>
          (originalRich[actualNamespace ?? ''] as Translator['rich'])?.(
            key,
            {
              ...tags,
              ...values,
            },
            formats,
          )
        }
      </RichText>
    )
  }
  $t.rich.notEmpty = (
    key: string | null | undefined,
    values?: TranslationValues | undefined,
    formats?: Partial<Formats> | undefined,
  ) => {
    if (isEmpty(key)) {
      return ''
    }

    const translatedContent = (
      originalRich[actualNamespace ?? ''] as Translator['rich']
    )(
      key,
      {
        ...getTags(),
        ...values,
      },
      formats,
    )

    return translatedContent === key ? (
      ''
    ) : (
      <RichText>
        {(tags) =>
          (originalRich[actualNamespace ?? ''] as Translator['rich'])(
            key,
            {
              ...tags,
              ...values,
            },
            formats,
          )
        }
      </RichText>
    )
  }

  if (!isNil(originalRich[actualNamespace ?? ''])) {
    $t.rich.simple = originalRich[actualNamespace ?? ''] as Translator['rich']
    $t.rich.simple.notEmpty = (
      key: string | null | undefined,
      values?: TranslationValues | undefined,
      formats?: Partial<Formats> | undefined,
    ) => translateIfNotEmpty(key, $t.rich.simple, values, formats)
  }

  $t.http = (error: HTTPError) => translateHTTPError(error, $t)

  return $t as Translator
}
